/*
 * <anfulu-size-gate>
 *
 * Shopify's accelerated checkout wallets (Shop Pay, Apple Pay, Google Pay, and
 * the Buy it now button) turn a product page into a one-tap purchase. On
 * Chinese-sized garments sold to Western bodies, that reflex tap is where
 * returns come from: the shopper never made a sizing decision, they just paid.
 *
 * So the wallets are hidden until the shopper explicitly confirms the size they
 * have selected. The normal add-to-cart button is never gated — it already has
 * a cart and a checkout page between the shopper and the purchase.
 *
 * Degradation contract:
 *   - The wallet container is hidden in CSS by default, not by this script.
 *     No JavaScript means no unlock, which means the wallets stay hidden and
 *     the shopper checks out through add-to-cart. Nothing is broken, one
 *     express route is simply unavailable.
 *   - The confirm control is likewise revealed by CSS only once this element
 *     upgrades (`data-gate-ready`), so a JS-less page never shows a dead
 *     button.
 *   - Changing any variant option re-locks the gate. A confirmation is for one
 *     specific size, not for the session.
 */

if (!customElements.get('anfulu-size-gate')) {
  customElements.define(
    'anfulu-size-gate',
    class AnfuluSizeGate extends HTMLElement {
      connectedCallback() {
        this.confirmButton = this.querySelector('[data-anfulu-gate-confirm]');
        this.resetButton = this.querySelector('[data-anfulu-gate-reset]');
        this.sizeLabels = this.querySelectorAll('[data-anfulu-gate-size]');
        this.status = this.querySelector('[data-anfulu-gate-status]');
        this.productInfo = this.closest('product-info');

        if (!this.confirmButton) return;

        this.onConfirm = this.onConfirm.bind(this);
        this.onReset = this.onReset.bind(this);
        this.onOptionChange = this.onOptionChange.bind(this);
        this.lock = this.lock.bind(this);

        this.confirmButton.addEventListener('click', this.onConfirm);
        this.resetButton?.addEventListener('click', this.onReset);

        // Dawn swaps the variant-selects DOM wholesale on every option change,
        // so listen on the section root rather than on the inputs themselves.
        this.changeRoot = this.productInfo || document;
        this.changeRoot.addEventListener('change', this.onOptionChange, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const sectionId = event?.data?.sectionId;
            if (sectionId != null && String(sectionId) !== String(this.dataset.sectionId)) return;
            this.lock();
          });
        }

        this.refreshSizeLabel();
        this.setAttribute('data-gate-state', 'locked');
        this.setAttribute('data-gate-ready', '');
      }

      disconnectedCallback() {
        this.confirmButton?.removeEventListener('click', this.onConfirm);
        this.resetButton?.removeEventListener('click', this.onReset);
        this.changeRoot?.removeEventListener('change', this.onOptionChange, true);
        this.unsubscribeVariantChange?.();
      }

      onOptionChange(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (!target.closest('variant-selects')) return;
        this.lock();
      }

      /*
       * Reads the size straight out of the live picker rather than from the
       * serialised variant, because the serialised variant lags a fetch behind
       * on option change.
       */
      readSelectedSize() {
        const optionName = this.dataset.sizeOptionName || '';
        const root = this.productInfo || document;
        const picker = root.querySelector('variant-selects');

        if (picker && optionName) {
          const radios = picker.querySelectorAll('input[type="radio"]');
          for (const radio of radios) {
            if (radio.checked && radio.dataset.optionName === optionName) {
              return (radio.value || '').trim();
            }
          }

          const selects = picker.querySelectorAll('select');
          for (const select of selects) {
            const option = select.options[select.selectedIndex];
            if (option && option.dataset.optionName === optionName) {
              return (option.value || '').trim();
            }
          }
        }

        // No size option on this product: fall back to the variant title, which
        // is what the shopper is actually confirming.
        const serialised = picker?.querySelector('script[data-selected-variant]');
        if (serialised) {
          try {
            const variant = JSON.parse(serialised.textContent);
            if (variant?.title && variant.title !== 'Default Title') return variant.title;
          } catch (error) {
            // A malformed payload is not worth breaking checkout over.
          }
        }

        return '';
      }

      refreshSizeLabel() {
        const size = this.readSelectedSize();
        this.currentSize = size;
        const label = size || this.dataset.fallbackLabel || 'this size';
        this.sizeLabels?.forEach((node) => {
          node.textContent = label;
        });
        return label;
      }

      lock() {
        this.setAttribute('data-gate-state', 'locked');
        this.refreshSizeLabel();
        if (this.status) this.status.textContent = '';
      }

      /* "Change size" re-locks the gate and hands focus back to the picker,
         because the shopper asked to change a size, not to read a message. */
      onReset() {
        this.lock();

        const optionName = this.dataset.sizeOptionName || '';
        const picker = (this.productInfo || document).querySelector('variant-selects');
        if (!picker) return;

        if (optionName) {
          // Dawn clips the radios rather than hiding them, so they are still
          // focusable and :focus-visible + label paints the ring.
          const radios = Array.from(picker.querySelectorAll('input[type="radio"]')).filter(
            (radio) => radio.dataset.optionName === optionName
          );
          const target = radios.find((radio) => radio.checked) || radios[0];
          if (target) {
            target.focus();
            target.nextElementSibling?.scrollIntoView?.({ block: 'nearest' });
            return;
          }

          const selects = picker.querySelectorAll('select');
          for (const select of selects) {
            const option = select.options[select.selectedIndex];
            if (option && option.dataset.optionName === optionName) {
              select.focus();
              return;
            }
          }
        }

        picker.querySelector('input, select')?.focus();
      }

      onConfirm() {
        const label = this.refreshSizeLabel();
        this.setAttribute('data-gate-state', 'unlocked');

        if (this.status) {
          this.status.textContent = this.currentSize
            ? `Size ${label} confirmed. Express checkout is available below.`
            : 'Confirmed. Express checkout is available below.';
        }

        this.renderWalletsIfNeeded();
        this.resetButton?.focus();
      }

      /*
       * Shopify renders the wallet buttons into `.shopify-payment-button` on
       * page load. If it skipped a container that was display:none, ask it to
       * run again now that the container is visible. Guarded so a
       * already-rendered container is never re-initialised.
       */
      renderWalletsIfNeeded() {
        const container = this.querySelector('.shopify-payment-button');
        if (!container) return;
        if (container.querySelector('iframe, shopify-accelerated-checkout, shopify-buy-it-now-button, button')) {
          return;
        }
        try {
          window.Shopify?.PaymentButton?.init?.();
        } catch (error) {
          // The wallets simply stay unavailable; add-to-cart is unaffected.
        }
      }
    }
  );
}
